import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Button,
  TextField,
  Alert,
  Skeleton,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthProvider';
import { useUserRoles } from '../auth/ProtectedRoute';
import userApi, { User, UpdateUserRequest } from '../api/userApi';
import logger from '../utils/logger';

const Profile: React.FC = () => {
  const { user: authUser, isAuthenticated } = useAuth();
  const userRoles = useUserRoles();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<UpdateUserRequest>({
    name: '',
    department: '',
    clearance_level: 1,
  });

  useEffect(() => {
    if (isAuthenticated) {
      loadUserProfile();
    }
  }, [isAuthenticated]);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const profile = await userApi.getCurrentUser();
      setUserProfile(profile);
      
      // Initialize form data
      setFormData({
        name: profile.name,
        department: profile.department || '',
        clearance_level: profile.clearance_level || 1,
      });

      logger.userAction('profile_viewed', {
        user_id: profile.id,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load profile';
      setError(errorMessage);
      logger.error('Profile load failed', { error: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setError(null);
    setSuccess(null);
    logger.userAction('profile_edit_started');
  };

  const handleCancel = () => {
    if (userProfile) {
      setFormData({
        name: userProfile.name,
        department: userProfile.department || '',
        clearance_level: userProfile.clearance_level || 1,
      });
    }
    setEditMode(false);
    setError(null);
    setSuccess(null);
    logger.userAction('profile_edit_cancelled');
  };

  const handleSave = async () => {
    if (!userProfile) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updatedProfile = await userApi.updateCurrentUser(formData);
      setUserProfile(updatedProfile);
      setEditMode(false);
      setSuccess('Profile updated successfully!');

      logger.userAction('profile_updated', {
        user_id: updatedProfile.id,
        changes: formData,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update profile';
      setError(errorMessage);
      logger.error('Profile update failed', { error: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof UpdateUserRequest) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: keyof UpdateUserRequest) => (
    event: SelectChangeEvent<number>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      setError('Image file must be smaller than 5MB');
      return;
    }

    try {
      setError(null);
      setUploadProgress(0);
      
      await userApi.uploadAvatar(file, setUploadProgress);
      
      // Reload profile to get updated avatar URL
      await loadUserProfile();
      setSuccess('Avatar updated successfully!');

      logger.userAction('avatar_uploaded', {
        file_size: file.size,
        file_type: file.type,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload avatar';
      setError(errorMessage);
      logger.error('Avatar upload failed', { error: errorMessage });
    } finally {
      setUploadProgress(0);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      setError(null);
      await userApi.deleteAvatar();
      await loadUserProfile();
      setSuccess('Avatar deleted successfully!');
      setShowDeleteDialog(false);

      logger.userAction('avatar_deleted');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete avatar';
      setError(errorMessage);
      logger.error('Avatar delete failed', { error: errorMessage });
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        User Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {uploadProgress > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            Uploading avatar...
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Profile Information
                </Typography>
                {!editMode && (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    disabled={loading}
                  >
                    Edit Profile
                  </Button>
                )}
              </Box>

              {loading ? (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Skeleton variant="circular" width={120} height={120} sx={{ mr: 3 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Skeleton variant="text" sx={{ fontSize: '2rem', mb: 1 }} />
                      <Skeleton variant="text" sx={{ mb: 1 }} />
                      <Skeleton variant="text" />
                    </Box>
                  </Box>
                  <Skeleton variant="rectangular" height={200} />
                </Box>
              ) : userProfile ? (
                <Box>
                  {/* Avatar and Basic Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                    <Box sx={{ position: 'relative', mr: 3 }}>
                      <Avatar
                        sx={{ width: 120, height: 120 }}
                        src={authUser?.profile?.picture}
                        alt={userProfile?.name || 'User'}
                      >
                        {(userProfile?.name || userProfile?.email || 'U')?.charAt?.(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="avatar-upload"
                        type="file"
                        onChange={handleAvatarUpload}
                      />
                      <label htmlFor="avatar-upload">
                        <IconButton
                          component="span"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            bgcolor: 'background.paper',
                            border: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <CameraIcon />
                        </IconButton>
                      </label>
                      {authUser?.profile?.picture && (
                        <IconButton
                          onClick={() => setShowDeleteDialog(true)}
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' },
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" gutterBottom>
                        {userProfile.name}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        {userProfile.email}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {userRoles.map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            variant="outlined"
                            size="small"
                            icon={<SecurityIcon />}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  {/* Editable Fields */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        value={editMode ? formData.name : userProfile.name}
                        onChange={handleInputChange('name')}
                        disabled={!editMode}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        value={userProfile.email}
                        disabled
                        helperText="Email cannot be changed"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Department"
                        value={editMode ? formData.department : (userProfile.department || 'Not specified')}
                        onChange={handleInputChange('department')}
                        disabled={!editMode}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth disabled={!editMode}>
                        <InputLabel>Clearance Level</InputLabel>
                        <Select
                          value={editMode ? formData.clearance_level : (userProfile.clearance_level || 1)}
                          label="Clearance Level"
                          onChange={handleSelectChange('clearance_level')}
                        >
                          <MenuItem value={1}>Level 1 - Basic</MenuItem>
                          <MenuItem value={2}>Level 2 - Intermediate</MenuItem>
                          <MenuItem value={3}>Level 3 - Advanced</MenuItem>
                          <MenuItem value={4}>Level 4 - Senior</MenuItem>
                          <MenuItem value={5}>Level 5 - Executive</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {editMode && (
                    <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Account Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ScheduleIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2">
                  Account Details
                </Typography>
              </Box>

              {loading ? (
                <Box>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" />
                </Box>
              ) : userProfile ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Account Created
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(userProfile.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(userProfile.updated_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      User ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {userProfile.id}
                    </Typography>
                  </Grid>
                </Grid>
              ) : null}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Avatar Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Avatar</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete your avatar? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteAvatar} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;